/**
 * NPC Studio API — quickcreate-async B 方案 (2026-05-25).
 *
 * 这里只放跨子应用共享的 endpoint (apps/xingye /create/edit 的 polling 需要).
 * 单纯 apps/agent 用的 endpoint 仍放 apps/agent/src/api/replicate.
 *
 * IDL manual sync from chat-agent (web-ugc 没用 @weaver/idl-typescript SDK, BigBoss
 * 19:49 + 20:08 sync 已对齐: 后端 thrift_gen 自动, 前端 web-ugc 手写跟 chat-agent IDL 同步).
 */
import { AxiosRequestConfig } from 'axios';

import { request } from '../request';

/**
 * QuickCreate async_mode task 轮询响应.
 *
 * 5 态 status (chat-agent 后端约定, BigBoss 19:47 + 19:54 verified):
 *   - pending     — task 已起, image + voice 都未完成, 全字段空
 *   - image_done  — image 单独完成 (image_url 已填), voice 还在跑, publish 未跑, npc_id 仍 0
 *   - voice_done  — voice 单独完成 (voice_id 已填), image 还在跑, publish 未跑, npc_id 仍 0
 *   - done        — image + voice + publish 都齐, image_url + voice_id + npc_id 全填
 *                   (chat_agent_internal_router.py:2965 `published_npc_id` 在此时拿到)
 *   - failed      — 失败, error_msg 填错因
 */
export interface GetNpcStudioCreateTaskResp {
  success?: boolean;
  error?: string;
  status?: 'pending' | 'image_done' | 'voice_done' | 'done' | 'failed' | string;
  image_url?: string;
  voice_id?: string;
  npc_id?: number;
  error_msg?: string;
  base_resp?: unknown;
}

/**
 * QuickCreate async_mode 异步轮询 (POST /weaver/api/v1/npc/studio/create/task/get).
 *
 * 不复用现有 /task/get (后者是 variants / batch_publish / asset_library 用的, 5 态 status 不同).
 *
 * 调用方 (packages/share/src/ui/Edit/useQuickCreatePolling):
 *   - 中间态 image_done / voice_done → hot-swap 直填 redux (updateImgInfo + mergeVoiceInfo),
 *     **不**调 getWorkspaceNpcDetail (publish 未跑, weaver workspace 仍空, npc_id 仍 0 必 404)
 *   - 终态 done 拿 npc_id → useAddQuery({npc_id}) 加 URL query, useEffect 监听 query.npc_id
 *     变化触发 dispatch(getWorkspaceNpcDetail({npc_id})) 整体覆盖
 *   - failed → 弹 Toast 含 error_msg + 跳回 /agent/quick-create (不引重试按钮)
 */
export const postGetNpcStudioCreateTask = async (
  data: { task_id: string },
  config?: AxiosRequestConfig
): Promise<GetNpcStudioCreateTaskResp> => {
  const url = '/weaver/api/v1/npc/studio/create/task/get';
  return await request.post(url, data, config || {});
};
